import { impliedRate, parseAmount, type AccountDto, type SwitchCurrencyResultDto } from '@koverta/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useToast } from '../components/toast';
import { Dialog, Flag, MoneyInput } from '../components/ui';
import { errorMessage, post } from '../lib/api';
import { useHousehold } from '../lib/household';
import { invalidateScopes, useRates } from '../lib/queries';

/**
 * Switch an account to the other currency (ynab-import.md §10): a new account with the real balance, a
 * cross-currency transfer of the old balance, and the old account closed with its history.
 */
export function SwitchCurrencyDialog({ account, onClose }: { account: AccountDto; onClose: () => void }) {
  const { fmt, today } = useHousehold();
  const { toast } = useToast();
  const navigate = useNavigate();
  const nbs = useRates().data?.current;
  const target = account.currency === 'RSD' ? 'EUR' : 'RSD';
  const [date, setDate] = useState(today);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const debt = account.working < 0;
  const typed = parseAmount(text, fmt.locale);
  const magnitude = typed === null ? null : Math.abs(typed);
  const empty = account.working === 0;
  // RSD per €1, implied by the two balances.
  const implied =
    magnitude && !empty
      ? account.currency === 'RSD'
        ? impliedRate(magnitude, account.working)
        : impliedRate(account.working, magnitude)
      : null;

  const save = async () => {
    if (magnitude === null) return setErr(`Enter the balance in ${target}`);
    if (empty !== (magnitude === 0))
      return setErr(
        empty
          ? 'This account is empty, so the new balance must be 0'
          : 'Enter the balance in the new currency',
      );
    setBusy(true);
    setErr(null);
    try {
      const r = await post<SwitchCurrencyResultDto>(`/api/accounts/${account.id}/switch-currency`, {
        date,
        balance: debt ? -magnitude : magnitude,
      });
      const preset = localStorage.getItem(`koverta.import.${r.oldAccountId}`);
      if (preset !== null) {
        localStorage.setItem(`koverta.import.${r.accountId}`, preset);
        localStorage.removeItem(`koverta.import.${r.oldAccountId}`);
      }
      invalidateScopes(['accounts', 'transactions', 'plan', 'activity', 'settings']);
      const n = r.reviewSchedules.length;
      toast(
        `${account.name} is now in ${target}` +
          (n
            ? ` · ${n} scheduled transaction${n === 1 ? ' was' : 's were'} converted — check the amounts`
            : ''),
      );
      onClose();
      navigate(`/accounts/${r.accountId}`);
    } catch (e) {
      setErr(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={`Switch ${account.name} to ${target}`}
      onClose={onClose}
      size="md"
      footer={
        <>
          <span className="sp" />
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy} onClick={save}>
            Switch to {target}
          </button>
        </>
      }
    >
      <div style={{ fontSize: 14 }}>
        Ovce na broju keeps one currency per account, so switching starts a new <Flag currency={target} />{' '}
        {target} account. Its history stays in a closed copy named “{account.name} ({account.currency}{' '}
        history)”, so past months don't change.
      </div>
      <div className="fgrid">
        <label className="field">
          Switch on
          <input
            className="input"
            type="date"
            value={date}
            max={today}
            onChange={(e) => setDate(e.target.value)}
          />
        </label>
        <div className="field">
          Current balance
          <div className="input" style={{ display: 'flex', alignItems: 'center' }} aria-readonly>
            {fmt.money(account.working, account.currency, 2)}
          </div>
        </div>
        <label className="field wide">
          {debt ? `What you owe in ${target}` : `The real balance in ${target}`} on that date
          <MoneyInput
            className="input r"
            autoFocus
            value={text}
            onChange={setText}
            aria-label={`Balance in ${target}`}
          />
        </label>
      </div>
      {implied ? (
        <div style={{ fontSize: 13 }} data-testid="switch-rate">
          Implied rate <b>€1 = {fmt.rate(implied)} RSD</b>
          {nbs ? <span className="muted"> · NBS {fmt.rate(nbs.rate)}</span> : null}
          {account.onBudget ? (
            <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
              Any difference from the NBS rate is realised FX and goes to Ready to Assign, never to a
              category.
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="muted" style={{ fontSize: 12.5 }}>
        Card mappings, the CSV import preset and scheduled transactions move to the new account. Scheduled
        amounts are converted at the implied rate; check them afterwards.
      </div>
      {err ? <div className="err-t">{err}</div> : null}
    </Dialog>
  );
}
